import * as React from 'react';
import {useState} from 'react';

import {clipboard, tag} from '@sail/icons/react/Icon';
import {useIntl as useSailIntl} from '@sail/intl';
import {defineMessages, useIntl} from 'react-intl';
import {useHover} from '@sail/react-aria';
import {Action, Divider, Icon, tokens, Tooltip, view} from '@sail/ui';
import {copyToClipboard} from '@sail/utils';
import type {AgentCallResponse} from '@stripe-internal/conversations-client';

import {useWidget} from '../../../Widget/useWidget';

type CouponCardProps = {
  agentCallResponse: AgentCallResponse;
  onClick?: () => void;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.coupon_card.title',
    description: 'Coupon Card title',
    defaultMessage: 'Coupon',
  },
  offLabel: {
    id: 'conversations_ui.coupon_card.off',
    description: 'Coupon card off label',
    defaultMessage: 'off',
  },
  expLabel: {
    id: 'conversations_ui.coupon_card.exp',
    description: 'Coupon card expiration prefix',
    defaultMessage: 'Exp',
  },
  copiedPromotionCode: {
    id: 'conversations_ui.coupon_card.copied_promotion_code',
    description: 'Toast message when promotion code is copied',
    defaultMessage: 'Copied promotion code',
  },
  copyCodeLabel: {
    id: 'conversations_ui.coupon_card.copy_code',
    description: 'Tooltip label for copy code button',
    defaultMessage: 'Copy code',
  },
});

type CouponNameProps = {
  id: string;
  name: string | null | undefined;
};

const CouponName: React.FC<CouponNameProps> = ({id, name}) => {
  if (name) {
    return <>{name}</>;
  }
  return <>{id}</>;
};

type CouponDiscountProps = {
  amountOff: number | null | undefined;
  currency: string | null | undefined;
  percentOff: number | null | undefined;
};

const CouponDiscount: React.FC<CouponDiscountProps> = ({
  amountOff,
  currency,
  percentOff,
}) => {
  const intl = useIntl();
  const sailIntl = useSailIntl();

  // Percentage discount
  if (percentOff) {
    return (
      <>
        {percentOff}
        {'% '}
        {intl.formatMessage(messages.offLabel)}
      </>
    );
  }

  // Fixed amount discount only
  if (amountOff && currency) {
    return (
      <>
        {sailIntl.formatCurrency(amountOff, {
          currency: currency.toUpperCase(),
        })}{' '}
        {intl.formatMessage(messages.offLabel)}
      </>
    );
  }

  return null;
};

type CouponCardData = any & {
  promotion_code: any | null | undefined;
};

const toCouponCardData = (
  response: AgentCallResponse,
): CouponCardData | null => {
  if (typeof response !== 'object' || !('object' in response)) {
    return null;
  }

  if (response.object === 'coupon') {
    const c = response as any & {
      promotion_code: any | null | undefined;
    };
    return c;
  }

  if (response.object === 'promotion_code') {
    const {coupon, ...p} = response as any & {coupon: any};

    return {...coupon, promotion_code: {...p, coupon}};
  }

  return null;
};

export const CouponCard = ({agentCallResponse, onClick}: CouponCardProps) => {
  const intl = useIntl();
  const sailIntl = useSailIntl();
  const widget = useWidget();
  const {hoverProps, isHovered} = useHover({});
  const copyIconRef = React.useRef<HTMLDivElement>(null);
  const [isCopied, setIsCopied] = useState(false);

  const coupon = toCouponCardData(agentCallResponse);

  if (!coupon) {
    return null;
  }

  return (
    <view.div
      data-testid="coupon-card"
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      {...hoverProps}
      onClick={() => {
        if (!coupon.id) {
          return;
        }

        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/coupons/${coupon.id}`,
            route: {
              name: 'COUPONS_DETAIL',
              params: {id: coupon.id},
            },
          },
        });
        onClick?.();
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'small',
          width: 'fill',
          minWidth: 'space.0',
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: 'space.75',
            alignItems: 'center',
          }}
        >
          <view.div
            css={{
              stack: 'x',
              gap: 'xsmall',
              alignItems: 'center',
              width: 'max',
            }}
          >
            <Icon
              icon={tag}
              size="xsmall"
              css={{color: isHovered ? 'action.primary' : 'subdued'}}
            />
            <view.span
              css={{
                color: 'subdued',
                font: 'label.small.emphasized',
              }}
            >
              {intl.formatMessage(messages.cardTitle)}
            </view.span>
          </view.div>
          <Divider />
          <view.span
            css={{
              font: 'label.small',
              color: 'subdued',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <CouponName id={coupon.id} name={coupon.name} />
          </view.span>
        </view.div>
        <view.div css={{stack: 'x', gap: 'xsmall', alignItems: 'baseline'}}>
          <view.span
            css={{
              font: 'heading.large',
            }}
          >
            <CouponDiscount
              amountOff={coupon.amount_off}
              currency={coupon.currency}
              percentOff={coupon.percent_off}
            />
          </view.span>
        </view.div>
        <view.div
          css={{
            stack: 'x',
            gap: 'medium',
            alignItems: 'center',
            width: 'fill',
            display: 'flex',
            overflow: 'hidden',
          }}
        >
          {coupon.promotion_code?.code ? (
            <Action
              onPress={() => {
                if (coupon.promotion_code?.code) {
                  copyToClipboard(coupon.promotion_code.code);
                  setIsCopied(true);
                  setTimeout(() => {
                    setIsCopied(false);
                  }, 2000);
                }
              }}
              css={{
                minWidth: 'space.0',
                flex: '1 1 0',
              }}
            >
              <Tooltip
                trigger={copyIconRef}
                placement="top"
                css={{
                  padding: 'small',
                  color: 'onFeedback.neutral.emphasized',
                }}
              >
                {isCopied
                  ? intl.formatMessage(messages.copiedPromotionCode)
                  : intl.formatMessage(messages.copyCodeLabel)}
              </Tooltip>
              <view.div
                css={{
                  stack: 'x',
                  gap: 'small',
                  justifyContent: 'space-between',
                  backgroundColor: isCopied ? tokens.color.brand[50] : 'offset',
                  alignItems: 'center',
                  paddingX: 'small',
                  paddingY: 'space.75',
                  color: 'text',
                  borderRadius: 'small',
                  overflow: 'hidden',
                  border: `1px solid ${tokens.color.neutral[50]}`,
                  ':hover': {
                    cursor: 'pointer',
                  },
                }}
              >
                <view.span
                  css={{
                    display: 'block',
                    font: 'label.small',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {coupon.promotion_code?.code}
                </view.span>
                <Icon
                  ref={copyIconRef}
                  icon={clipboard}
                  size="xsmall"
                  css={{
                    fill: 'action.primary',
                    flexShrink: 0,
                  }}
                />
              </view.div>
            </Action>
          ) : null}
          {coupon.redeem_by ? (
            <view.div
              css={{
                stack: 'x',
                minWidth: 'space.0',
                maxWidth: 'max',
                flex: '2 1 0',
              }}
            >
              <view.span
                css={{
                  font: 'label.small',
                  color: 'subdued',
                  whiteSpace: 'nowrap',
                }}
              >
                {intl.formatMessage(messages.expLabel)}{' '}
                {sailIntl.formatDateTime(new Date(coupon.redeem_by * 1000), {
                  style: 'medium-year-month-day',
                })}
              </view.span>
            </view.div>
          ) : null}
        </view.div>
      </view.div>
    </view.div>
  );
};
